import React from 'react';
import '../styles/FAQSection.css';

const typingFaqs = [
  {
    question: 'What is CPM in typing?',
    answer: 'CPM stands for Characters Per Minute. It measures the number of characters you can type correctly in one minute, including spaces and punctuation.',
  },
  {
    question: 'What is WPM in typing?',
    answer: 'WPM stands for Words Per Minute. It measures the number of words you can type correctly in one minute. A standard word is typically considered to be 5 characters (including spaces).',
  },
  {
    question: 'What is average CPM in typing?',
    answer: 'Average CPM varies greatly depending on age, experience, and profession. For a casual typist, it might be around 200-300 CPM (40-60 WPM). For professional typists, it can be 400+ CPM (80+ WPM).',
  },
  {
    question: 'What is average WPM in typing?',
    answer: 'The average typing speed is typically around 40 WPM. However, this can range from 20 WPM for beginners to over 75 WPM for experienced typists.',
  },
  {
    question: 'What is good CPM typing speed?',
    answer: 'A good CPM typing speed is generally considered to be above 350 CPM (70 WPM). Speeds above 450 CPM (90 WPM) are often considered excellent.',
  },
  {
    question: 'What is good WPM typing speed?',
    answer: 'A good WPM typing speed is generally considered to be above 70 WPM. Speeds of 90 WPM or higher are considered excellent and are often required for professions like transcription.',
  },
  {
    question: 'Is typing 40 WPM good?',
    answer: 'Typing 40 WPM is considered an average speed. It\'s a good starting point for most daily tasks, but there\'s room for improvement, especially for professional roles.',
  },
  {
    question: 'Is typing 70 words a minute fast?',
    answer: 'Yes, typing 70 WPM is considered a fast typing speed. This speed is well above average and proficient for most professional tasks.',
  },
  {
    question: 'Is a 120 WPM typing speed good?',
    answer: 'Typing at 120 WPM is an excellent and very fast typing speed. This speed is typically achieved by professional typists and is considered expert level.',
  },
  {
    question: 'Is 75 WPM a fast typing speed?',
    answer: 'Yes, 75 WPM is definitely a fast typing speed. It is considered a very efficient speed for most computer-based tasks.',
  },
];

const clickFaqs = [
  {
    question: 'What is CPS in clicking?',
    answer: 'CPS stands for Clicks Per Second. It measures how many times you can click your mouse button in one second.',
  },
  {
    question: 'What is average CPS in clicking?',
    answer: 'Average CPS varies depending on the test duration and clicking method. For a quick test (like 1s or 5s), averages might be higher (around 5-7 CPS). Over longer durations (like 60s), the average might be lower (around 4-6 CPS).',
  },
  {
    question: 'What is good CPS clicking speed?',
    answer: 'A good CPS varies by duration, but generally, speeds consistently above 7 CPS are considered good, and speeds above 10 CPS are excellent, especially over longer durations.',
  },
  {
    question: 'What is a click test?',
    answer: 'A click test (or CPS test) measures how many clicks per second (CPS) you can achieve with your mouse. It\'s used to evaluate clicking speed and consistency, often for gaming or general mouse dexterity.',
  },
  {
    question: 'What is the purpose of a click test?',
    answer: 'The purpose of a click test is to measure and improve your clicking speed and accuracy. It\'s popular among gamers (especially for genres requiring rapid clicks), but also useful for assessing general mouse control and endurance.',
  },
  {
    question: 'How to do a click test?',
    answer: 'To do a click test, simply click the designated area on the screen as many times as you can within the given time limit. The test begins with your first click. Your score will be calculated as Clicks Per Second (CPS).',
  },
];

const colorMemoryFaqs = [
  {
    question: 'How does Color Memory work?',
    answer: 'A random color is shown for 5 seconds. After it disappears, use the hue, saturation, and lightness sliders to recreate it from memory, then submit your guess to see your score.',
  },
  {
    question: 'How is the score calculated?',
    answer: 'Your score is based on how close your guess is to the target color in RGB space. A perfect match scores 100. The farther the colors are apart, the lower your score.',
  },
  {
    question: 'Is there a time limit for picking a color?',
    answer: 'No. You only have 5 seconds to memorize the color. After that, take as long as you need to adjust the sliders before submitting.',
  },
  {
    question: 'Is my best score saved?',
    answer: 'Yes. Your highest score is stored in this browser with local storage so you can try to beat it later on the same device.',
  },
];

const higherOrLowerFaqs = [
  {
    question: 'How do you play Higher or Lower?',
    answer: 'The center prompt asks “Which number is higher?” or “Which number is lower?” Click the side with the bigger or smaller number. Each card shows a country with either population or territory size.',
  },
  {
    question: 'Can population and territory size be compared in one round?',
    answer: 'Yes. You compare the numbers only. For example, Latvia’s population might be 1,871,871 and another country’s territory size 64,589 — population is higher because 1,871,871 is the bigger number, even though the labels are different.',
  },
  {
    question: 'Where does the population data come from?',
    answer: 'The game uses approximate 2024 United Nations and World Bank population estimates. Figures are rounded snapshots, so they may differ slightly from the latest census or live counters.',
  },
  {
    question: 'Where do territory size figures come from?',
    answer: 'Country territory sizes come from standard geographic references, measured in square kilometers (km²). You still compare the raw numbers against population figures.',
  },
  {
    question: 'Is my highscore saved?',
    answer: 'Yes. Your best score is stored in this browser with local storage, so you can come back later and try to beat it on the same device.',
  },
  {
    question: 'Can I play Higher or Lower on mobile?',
    answer: 'Yes. Tap the left or right side. On a keyboard you can also use the left and right arrow keys.',
  },
];

const stopTheWheelFaqs = [
  {
    question: 'How do you play Stop the Wheel?',
    answer: 'There are 30 levels. Stop the wheel when the purple target zone lines up with the pointer at the top. Hit the sector to clear the level and move on. Miss, and you retry the same level.',
  },
  {
    question: 'How is the score calculated?',
    answer: 'A perfect alignment with the center of the target zone scores up to 100. The farther you are from the target center in degrees, the lower your score. You still need to land inside the purple sector to advance.',
  },
  {
    question: 'Does the wheel get harder each level?',
    answer: 'Yes. Level 1 has the largest, slowest target. Each later level shrinks the sector and spins the wheel faster. Level 30 is tight but still hittable if you time it.',
  },
  {
    question: 'Is my progress saved?',
    answer: 'Yes. Your current level, best level cleared, and best score are stored in this browser with local storage on the same device.',
  },
];

const FAQSection = ({ mode = 'typing' }) => {
  const faqs = mode === 'click'
    ? clickFaqs
    : mode === 'higherOrLower'
      ? higherOrLowerFaqs
      : mode === 'colorMemory'
        ? colorMemoryFaqs
        : mode === 'stopTheWheel'
          ? stopTheWheelFaqs
          : typingFaqs;
  const title = mode === 'click'
    ? 'Click Test FAQs'
    : mode === 'higherOrLower'
      ? 'Higher or Lower FAQs'
      : mode === 'colorMemory'
        ? 'Color Memory FAQs'
        : mode === 'stopTheWheel'
          ? 'Stop the Wheel FAQs'
          : 'Typing Test FAQs';

  return (
    <section className="faq-section">
      <h2>{title}</h2>
      <div className="faq-list">
        {faqs.map((faq, index) => (
          <div className="faq-item" key={index}>
            <h3>{faq.question}</h3>
            <p>{faq.answer}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FAQSection;
export { higherOrLowerFaqs, colorMemoryFaqs, stopTheWheelFaqs }; 